import React, { useCallback, useMemo } from 'react';
import { ShopExtended } from '../../../domain/types';

interface CatalogFiltersProps {
  filter: string;
  setFilter: (filter: string) => void;
  selectedShop: string | null;
  setSelectedShop: (shopId: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy: 'featured' | 'priceAsc' | 'priceDesc' | 'newest';
  setSortBy: (sort: 'featured' | 'priceAsc' | 'priceDesc' | 'newest') => void;
  filterCategories: string[];
  shops: ShopExtended[];
  totalItems: number;
  showLeftArrow: boolean;
  showRightArrow: boolean;
  filterScrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  onScrollFilters: (direction: 'left' | 'right') => void;
  setShowAdvancedFilters: (show: boolean) => void;
  isFavoritesView: boolean;
  hasFavorites: boolean;
  user: any;
  followingShops: any[];
}

export const CatalogFilters: React.FC<CatalogFiltersProps> = React.memo(({
  filter,
  setFilter,
  selectedShop,
  setSelectedShop,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  filterCategories,
  shops,
  totalItems,
  showLeftArrow,
  showRightArrow,
  filterScrollRef,
  onScroll,
  onScrollFilters,
  setShowAdvancedFilters,
  isFavoritesView,
  hasFavorites,
  user,
  followingShops
}) => {
  const handleClearShopFilter = useCallback(() => {
    setSelectedShop(null);
  }, [setSelectedShop]);

  const handleSortClick = useCallback(() => {
    const newSort = 
      sortBy === 'featured' ? 'newest' : 
      sortBy === 'newest' ? 'priceAsc' : 
      sortBy === 'priceAsc' ? 'priceDesc' : 'featured';
    setSortBy(newSort);
  }, [setSortBy, sortBy]);

  const sortLabel = useMemo(() => {
    switch(sortBy) {
      case 'featured': return 'Destacados';
      case 'newest': return 'Nuevos';
      case 'priceAsc': return 'Precio: Menor';
      case 'priceDesc': return 'Precio: Mayor';
      default: return 'Destacados';
    }
  }, [sortBy]);

  // Don't render if viewing favorites with items (clean UI)
  if (isFavoritesView && hasFavorites) {
    return null;
  }

  return (
    <div className="w-full border-b border-black/5 dark:border-white/5">
      <div className="relative group/filters">
        <button 
          onClick={() => onScrollFilters('left')}
          className={`absolute left-1 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-surface-light dark:bg-surface-dark border border-black/10 dark:border-white/10 rounded-full shadow-lg flex items-center justify-center text-primary transition-all duration-300 active:scale-90 ${showLeftArrow ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}
        >
          <span className="material-symbols-outlined text-2xl">chevron_left</span>
        </button>
        <button 
          onClick={() => onScrollFilters('right')}
          className={`absolute right-1 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-surface-light dark:bg-surface-dark border border-black/10 dark:border-white/10 rounded-full shadow-lg flex items-center justify-center text-primary transition-all duration-300 active:scale-90 ${showRightArrow ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}
        >
          <span className="material-symbols-outlined text-2xl">chevron_right</span>
        </button>
        <div 
          ref={filterScrollRef} 
          onScroll={onScroll} 
          className="grid grid-rows-2 grid-flow-col gap-y-2 gap-x-2 overflow-x-auto no-scrollbar py-3 px-4 w-full"
        >
          {/* My Shops - Show only if user is following shops */}
          {user && followingShops.length > 0 && (
            <button
              onClick={() => { setFilter('Mis Tiendas'); setSelectedShop('following'); setSearchQuery(''); }}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold transition-all border flex items-center gap-1 ${
                filter === 'Mis Tiendas' && selectedShop === 'following'
                  ? 'bg-purple-500 border-purple-500 text-white shadow-md'
                  : 'bg-transparent border-purple-300 dark:border-purple-500/30 text-purple-600 dark:text-purple-400 hover:border-purple-400'
              }`}
            >
              <span className="material-symbols-outlined text-[12px]">favorite</span>
              Mis Tiendas ({followingShops.length})
            </button>
          )}
          {filterCategories.map((cat, i) => (
            <button 
              key={i} 
              data-category={cat}
              onClick={() => { setFilter(cat); setSearchQuery(''); setSelectedShop(null); }}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold transition-all border ${
                filter === cat && !selectedShop
                  ? 'bg-text-main dark:bg-white text-white dark:text-black border-transparent shadow-md' 
                  : 'bg-transparent border-gray-300 dark:border-white/10 text-text-secondary dark:text-gray-400 hover:border-gray-400'
              }`}
            >
              {cat}
            </button>
          ))}
          {shops.map((shop) => (
            <button
              key={shop.id}
              onClick={() => { setSelectedShop(shop.id); setFilter('Todos'); }}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold transition-all border flex items-center gap-1 ${
                selectedShop === shop.id
                  ? 'bg-orange-500 border-orange-500 text-white shadow-md'
                  : 'bg-transparent border-gray-300 dark:border-white/10 text-text-secondary dark:text-gray-400 hover:border-orange-400'
              }`}
            >
            <span className="material-symbols-outlined text-[12px]">storefront</span>
            {shop.name ? (shop.name.length > 15 ? shop.name.substring(0, 15) + '...' : shop.name) : 'Tienda'}
            </button>
          ))}
          {selectedShop && (
            <button
              onClick={handleClearShopFilter}
              className="whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold transition-all border border-red-300 bg-red-50 text-red-500 hover:bg-red-100"
            >
              × Limpiar filtro
            </button>
          )}
        </div>
      </div>
      <div className="px-4 py-2 flex justify-between items-center text-xs text-text-secondary dark:text-gray-400 bg-black/5 dark:bg-white/5">
        <div className="flex items-center gap-3">
          <span className="font-bold whitespace-nowrap">{totalItems} productos cargados</span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowAdvancedFilters(true)}
            className="flex items-center gap-1 font-bold hover:text-primary transition-colors select-none px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5"
          >
            <span className="material-symbols-outlined text-[16px]">filter_list</span>
            Filtros
          </button>
          <button 
            onClick={handleSortClick}
            className="flex items-center gap-1 font-bold hover:text-primary transition-colors select-none px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5"
          >
            <span className="material-symbols-outlined text-[16px]">sort</span>
            {sortLabel}
          </button>
        </div>
      </div>
    </div>
  );
});

CatalogFilters.displayName = 'CatalogFilters';
